import React, { useState, useEffect } from 'react';
import { Factory, Flame, Wind, AlertTriangle, Maximize2 } from 'lucide-react';

interface EmissionZone {
  id: string;
  name: string;
  x: number;
  y: number;
  intensity: number;
  type: 'furnace' | 'kiln' | 'storage' | 'transport';
}

const zones: EmissionZone[] = [
  { id: '1', name: 'Blast Furnace', x: 15, y: 20, intensity: 85, type: 'furnace' },
  { id: '2', name: 'Steel Kiln', x: 40, y: 25, intensity: 72, type: 'kiln' },
  { id: '3', name: 'Cement Kiln', x: 65, y: 20, intensity: 68, type: 'kiln' },
  { id: '4', name: 'Waste Storage', x: 25, y: 60, intensity: 35, type: 'storage' },
  { id: '5', name: 'Transport Hub', x: 70, y: 65, intensity: 42, type: 'transport' },
  { id: '6', name: 'Cooling Tower', x: 50, y: 50, intensity: 28, type: 'storage' },
];

export default function DigitalTwin() {
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const [liveData, setLiveData] = useState(zones);
  const mapRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveData(prev => prev.map(zone => ({
        ...zone,
        intensity: Math.max(20, Math.min(100, zone.intensity + (Math.random() - 0.5) * 5))
      })));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const getColor = (intensity: number) => {
    if (intensity > 70) return { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500' };
    if (intensity > 50) return { bg: 'bg-yellow-500', text: 'text-yellow-500', border: 'border-yellow-500' };
    return { bg: 'bg-green-500', text: 'text-green-500', border: 'border-green-500' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Factory className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Digital Twin - Plant Visualization</h1>
          </div>
          <p className="text-gray-400">Real-time emission monitoring and facility heat map</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Heat Map Canvas */}
          <div className="lg:col-span-3 bg-gray-800 rounded-lg p-6" ref={mapRef}>
            <div className="relative w-full bg-gray-900 rounded-lg overflow-hidden" style={{ height: '600px' }}>
              {/* Grid Background */}
              <svg className="absolute inset-0 w-full h-full opacity-20">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="gray" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>

              {/* Heat Map Zones */}
              {liveData.map((zone) => {
                const colors = getColor(zone.intensity);
                const size = 80 + (zone.intensity / 100) * 40;
                
                return (
                  <div key={zone.id}>
                    {/* Heat Glow */}
                    <div
                      className={`absolute rounded-full blur-3xl ${colors.bg} opacity-30 transition-all duration-1000`}
                      style={{
                        left: `${zone.x}%`,
                        top: `${zone.y}%`,
                        width: `${size * 2}px`,
                        height: `${size * 2}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                    
                    {/* Zone Marker */}
                    <div
                      className={`absolute cursor-pointer transition-all duration-300 ${
                        hoveredZone === zone.id ? 'scale-110' : 'scale-100'
                      }`}
                      style={{
                        left: `${zone.x}%`,
                        top: `${zone.y}%`,
                        transform: 'translate(-50%, -50%)',
                      }}
                      onMouseEnter={() => setHoveredZone(zone.id)}
                      onMouseLeave={() => setHoveredZone(null)}
                    >
                      <div className={`relative w-16 h-16 rounded-full ${colors.bg} flex items-center justify-center shadow-lg border-4 ${colors.border} animate-pulse`}>
                        {zone.type === 'furnace' && <Flame className="w-8 h-8 text-white" />}
                        {zone.type === 'kiln' && <Factory className="w-8 h-8 text-white" />}
                        {zone.type === 'storage' && <AlertTriangle className="w-8 h-8 text-white" />}
                        {zone.type === 'transport' && <Wind className="w-8 h-8 text-white" />}
                      </div>
                      
                      {/* Tooltip */}
                      {hoveredZone === zone.id && (
                        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-2xl whitespace-nowrap z-10 border border-gray-700">
                          <div className="font-bold text-lg mb-1">{zone.name}</div>
                          <div className="text-gray-300">CO₂: {zone.intensity.toFixed(1)} tCO₂e/hr</div>
                          <div className={`text-sm mt-1 ${zone.intensity > 70 ? 'text-red-400' : 'text-green-400'}`}>
                            {zone.intensity > 70 ? '⚠ High Emission' : '✓ Normal Range'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Label */}
                    <div
                      className="absolute text-white text-xs font-medium bg-gray-800 px-2 py-1 rounded"
                      style={{
                        left: `${zone.x}%`,
                        top: `${zone.y + 8}%`,
                        transform: 'translateX(-50%)',
                      }}
                    >
                      {zone.name}
                    </div>
                  </div>
                );
              })}

              {/* Plant Outline */}
              <div className="absolute inset-0 border-2 border-dashed border-gray-600 rounded-lg m-4 pointer-events-none">
                <div className="absolute top-2 left-2 text-gray-500 text-sm">Plant Boundary</div>
              </div>
            </div>

            {/* Controls */}
            <div className="mt-4 flex gap-4">
              <button onClick={toggleFullscreen} className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700">
                <Maximize2 className="w-4 h-4" />
                Full Screen
              </button>
              <div className="flex-1 bg-gray-700 rounded-lg px-4 py-2 flex items-center justify-between">
                <span className="text-gray-300 text-sm">Live Updates: ON</span>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-4">
            {/* Live Stats */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Live Emission Zones
              </h3>
              <div className="space-y-3">
                {liveData.map((zone) => {
                  const colors = getColor(zone.intensity);
                  return (
                    <div key={zone.id} className="bg-gray-700 rounded p-3">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-white text-sm font-medium">{zone.name}</span>
                        <span className={`text-xs px-2 py-1 rounded ${colors.bg} text-white`}>
                          {zone.intensity.toFixed(0)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${colors.bg} transition-all duration-1000`}
                          style={{ width: `${zone.intensity}%` }}
                        />
                      </div>
                      <div className="text-gray-400 text-xs mt-1">
                        {zone.intensity.toFixed(1)} tCO₂e/hr
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-white font-bold mb-3">Heat Map Legend</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-500"></div>
                  <span className="text-gray-300 text-sm">Critical (&gt;70%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-yellow-500"></div>
                  <span className="text-gray-300 text-sm">Warning (50-70%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-500"></div>
                  <span className="text-gray-300 text-sm">Normal (&lt;50%)</span>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="text-white font-bold mb-3">Plant Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-300">
                  <span>Total Zones:</span>
                  <span className="text-white font-medium">{liveData.length}</span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Avg Emission:</span>
                  <span className="text-white font-medium">
                    {(liveData.reduce((a, b) => a + b.intensity, 0) / liveData.length).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-gray-300">
                  <span>Critical Zones:</span>
                  <span className="text-red-400 font-medium">
                    {liveData.filter(z => z.intensity > 70).length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
